<script setup lang="ts">
import { LOCALE } from "core/interfaces";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { translationNormalize } from "~/common/helpers/translation-adapter";
import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateCollectionByIdDto } from "~/common/interfaces/dto/collections/update-collection-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useCollectionsStore } from "~/stores/collections";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const collectionsStore = useCollectionsStore();
const { collectionsModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<UpdateCollectionByIdDto>({
	initialValues: translationNormalize(collectionsModalEdit.collection.translations, ["name"]),
	validationSchema: yup.object({
		name: yup.object({
			uk: yup
				.string()
				.optional(),
			en: yup
				.string()
				.optional()
		})
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateCollectionByIdDto) => {
	collectionsStore.currentCollection = await collectionsStore.patchById(collectionsModalEdit.collection.id, dto);

	collectionsModalEdit.closeModal();

	if (route.name === "products-collections") {
		collectionsStore.getAll({}, collectionsStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.COLLECTIONS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.COLLECTIONS.MODAL_EDIT.alert.title", { collectionName: collectionsModalEdit.collection.name }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: nameUK } = useField<string>("name.uk");
const { value: nameEN } = useField<string>("name.en");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.COLLECTIONS.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="collectionsModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-6">
			<span class="font-semibold text-sm">
				Українською
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameUK"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 1, { locale: LOCALE.UKRAINIAN })"
					:error="errors['name.uk']"
				/>
			</div>
		</div>
		<div class="mt-4">
			<span class="font-semibold text-sm">
				English
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameEN"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 1, { locale: LOCALE.ENGLISH })"
					:error="errors['name.en']"
				/>
			</div>
		</div>
	</AppModal>
</template>
